// react-magnetic-di - temporary comment, for auto dependency injection in tests
import React, { useEffect, useMemo } from 'react';

import { di } from 'react-magnetic-di';

import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';

import { useAdjustmentsContextActions } from '../../../controllers/adjustments-context';
import { ExecutionContextProvider } from '../../../controllers/execution-context';
import { useViewType } from '../../../controllers/issue-adjustments/hooks/use-view-type';
import { TenantContextProvider } from '../../../controllers/tenant-context';
import {
	isExecutionContextSupported,
	isTriggerPointSupported,
} from '../../../controllers/view-configuration';
import { AppsRenderer } from '../fragment/apps-renderer';
import { useAppsToRender } from '../fragment/apps-renderer/use-apps-to-render';
import { useContextCleanup } from '../fragment/use-contexts-cleanup';
import { useEnrichedExecutionContext } from '../fragment/use-enriched-execution-context';

import type {
	RootWithDataCleanupProps,
	RootWithDataProps,
	UiModificationsRootWithDataProps,
} from './types';

/**
 * UI modifications root component that accepts data directly.
 * This is currently duplicate of the UiModificationsRootFragment component. We have to extract the common logic to a separate hook.
 */
export const UiModificationsRootWithData = (
	props: UiModificationsRootWithDataProps,
): React.JSX.Element | null => {
	const executionContext = useEnrichedExecutionContext({
		executionContext: props.executionContext,
	});
	const { executionContextKey, triggerPointKey } = props;

	const viewType = useViewType();
	const { setShouldUiModificationsLoad } = useAdjustmentsContextActions();

	const shouldLoad =
		executionContext &&
		executionContextKey &&
		isTriggerPointSupported(viewType, triggerPointKey) &&
		isExecutionContextSupported(executionContext);

	useEffect(() => {
		if (shouldLoad) {
			setShouldUiModificationsLoad(shouldLoad ? 'yes' : 'no');
		}
	}, [setShouldUiModificationsLoad, shouldLoad, executionContextKey]);

	if (!shouldLoad) {
		return null;
	}

	return (
		<RootWithDataCleanup
			{...props}
			executionContext={executionContext}
			executionContextKey={executionContextKey}
		/>
	);
};

const RootWithDataCleanup = (props: RootWithDataCleanupProps) => {
	di(AnalyticsContext);

	const {
		cloudId,
		activationId,
		executionContextKey,
		triggerPointKey,
		executionContext,
		numberOfSupportedFields,
		resetFormFieldsCounter,
	} = props;

	useContextCleanup({ executionContextKey, numberOfSupportedFields, resetFormFieldsCounter });

	const tenantContext = useMemo(
		() => ({
			cloudId,
			activationId,
		}),
		[cloudId, activationId],
	);

	const analyticsContextData = useMemo(
		() => ({
			attributes: {
				triggerPointKey,
			},
		}),
		[triggerPointKey],
	);

	return (
		<AnalyticsContext data={analyticsContextData}>
			<ExecutionContextProvider value={executionContext}>
				<TenantContextProvider value={tenantContext}>
					<RootWithData {...props} />
				</TenantContextProvider>
			</ExecutionContextProvider>
		</AnalyticsContext>
	);
};

const RootWithData = ({
	uiModificationsData,
	ForgeAppRenderer,
	executionContextKey,
}: RootWithDataProps) => {
	di(AppsRenderer);

	const appsToRender = useAppsToRender({
		data: uiModificationsData,
		executionContextKey,
	});

	return <AppsRenderer apps={appsToRender} ForgeAppRenderer={ForgeAppRenderer} />;
};
